import { PrimaryButton } from "components/Button/style";
import { ModalConfigColunas } from "components/ModalConfigColunas";
import { useConfigColunas } from "components/ModalConfigColunas/useConfigColunas";
import { Table } from "containers/Table";
import { IColum } from "containers/Table/interfaces";
import { ReactNode, useEffect, useState } from "react";
import { FaColumns, FaUpload } from "react-icons/fa";
import { DirecaoOrdenacao } from "../../interfaces/IOrdenacao";
import {
  BotaoIcone,
  Container,
  Header,
  HeaderContainerBotoes,
  TextoHeader,
} from "./style";

export interface IColunaTabelaPadrao<T> extends IColum<T> {
  chave: string;
  titulo: string;
}

interface IProps<T> {
  titulo: string;
  chaveConfigColunas: string;
  colunas: IColunaTabelaPadrao<T>[];
  dados: T[];
  carregando: boolean;
  totalRegistros: number;
  limite: number;
  colunaOrdenadaInicial?: string;
  direcaoOrdenacaoInicial?: DirecaoOrdenacao;
  reiniciarPaginacao?: boolean;
  paginaInicial?: number;
  limparSelecao?: number;
  onMudarPagina: (pagina: number) => void;
  onMudarLimite: (limite: number) => void;
  onOrdenar?: (campo: string, direcao: DirecaoOrdenacao) => void;
  onExportar?: () => void;
  exportando?: boolean;
  botoesExtras?: ReactNode;
  selecionavel?: boolean;
  onSelecionar?: (linhas: T[]) => void;
  limitarALargura?: boolean;
}

export const OPCOES_LIMITE_TABELA_PADRAO = [50, 100, 300];

export function TabelaPadrao<T>({
  titulo,
  chaveConfigColunas,
  colunas,
  dados,
  carregando,
  totalRegistros,
  limite,
  colunaOrdenadaInicial,
  direcaoOrdenacaoInicial,
  reiniciarPaginacao,
  paginaInicial,
  limparSelecao = 0,
  onMudarPagina,
  onMudarLimite,
  onOrdenar,
  onExportar,
  exportando = false,
  botoesExtras,
  selecionavel = false,
  onSelecionar,
  limitarALargura = false,
}: IProps<T>) {
  const [mostrarConfigColunas, setMostrarConfigColunas] = useState(false);
  const [limparSelecaoTable, setLimparSelecaoTable] = useState(false);

  // O clearSelectedRows do Table só limpa quando o valor passa para true.
  useEffect(() => {
    if (limparSelecao) setLimparSelecaoTable(true);
  }, [limparSelecao]);

  useEffect(() => {
    if (limparSelecaoTable) setLimparSelecaoTable(false);
  }, [limparSelecaoTable]);
  const { configColunas, isVisible, salvarConfigColunas } = useConfigColunas(
    chaveConfigColunas,
    colunas.map(({ chave }) => ({ nome: chave, exibir: true }))
  );
  const labelsColunas = Object.fromEntries(
    colunas.map(({ chave, titulo: tituloColuna }) => [chave, tituloColuna])
  );
  const colunasTabela: IColum<T>[] = colunas.map(
    ({ chave, titulo: tituloColuna, ...coluna }) => ({
      ...coluna,
      name: <span>{tituloColuna}</span>,
      omit: !isVisible(chave),
    })
  );
  const indiceOrdenada = colunas.findIndex(
    ({ chave }) => chave === colunaOrdenadaInicial
  );

  return (
    <Container $limitarALargura={limitarALargura}>
      <ModalConfigColunas
        show={mostrarConfigColunas}
        handleClose={() => setMostrarConfigColunas(false)}
        configColunas={configColunas}
        onSave={salvarConfigColunas}
        labels={labelsColunas}
      />
      <Header>
        <TextoHeader>{titulo}</TextoHeader>
        <HeaderContainerBotoes>
          {onExportar && (
            <BotaoIcone>
              <PrimaryButton
                type="button"
                outline
                variante="primary"
                title="Exportar"
                onClick={onExportar}
                disabled={exportando}
              >
                <FaUpload />
              </PrimaryButton>
            </BotaoIcone>
          )}
          <BotaoIcone>
            <PrimaryButton
              type="button"
              outline
              variante="primary"
              title="Configurar colunas"
              onClick={() => setMostrarConfigColunas(true)}
            >
              <FaColumns />
            </PrimaryButton>
          </BotaoIcone>
          {botoesExtras}
        </HeaderContainerBotoes>
      </Header>
      <Table
        keyField="id"
        columns={colunasTabela}
        data={dados}
        loading={carregando}
        totalRows={totalRegistros}
        pagination
        paginationRowsPerPageOptions={OPCOES_LIMITE_TABELA_PADRAO}
        rowsPerPage={limite}
        defaultSortFieldId={
          indiceOrdenada >= 0 ? indiceOrdenada + 1 : undefined
        }
        defaultSortAsc={direcaoOrdenacaoInicial !== "DESC"}
        paginationDefaultPage={paginaInicial}
        paginationResetDefaultPage={reiniciarPaginacao}
        onChangePage={onMudarPagina}
        onChangeRowsPerPage={onMudarLimite}
        handleSortFunction={(coluna, direcao) =>
          onOrdenar?.(
            String(coluna.sortField ?? ""),
            String(direcao).toUpperCase() === "DESC" ? "DESC" : "ASC"
          )
        }
        noSubHeader
        selectableRows={selecionavel}
        setRowsSelected={onSelecionar}
        clearSelectedRows={limparSelecaoTable}
      />
    </Container>
  );
}
